// Lightweight markdown → HTML renderer (XSS-safe), ported from the PE demo's
// Cipher AI chat (KaTeX stripped — the portal has no math needs). Handles
// headings (#–####), bullet/ordered lists, bold, italic, bold-italic, inline
// code, fenced code blocks, links, `---` rules, and GitHub-flavoured pipe
// tables. Inputs are HTML-escaped first, so direct injection of <script>
// etc. is impossible.

function escHtml(s: string): string {
  return String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

export function md(raw: string | null | undefined): string {
  if (!raw) return '<span style="opacity:.4">…</span>';
  let t = raw;

  // ── Fenced code blocks: stash before HTML-escaping the rest, so the
  //     inline-code/regex passes below don't chew through code contents.
  const codeBlocks: string[] = [];
  t = t.replace(/```(\w*)\n([\s\S]*?)```/g, (_m, _lang, code) => {
    const html = `<pre style="background:var(--color-bg-secondary);border:1px solid var(--color-border-light);border-radius:6px;padding:10px 12px;overflow-x:auto;font-size:.82em;line-height:1.5;margin:8px 0"><code>${escHtml(
      code.replace(/\s+$/u, ""),
    )}</code></pre>`;
    const idx = codeBlocks.push(html) - 1;
    return `__CODE_${idx}__`;
  });

  t = escHtml(t);

  t = t.replace(
    /\[([^\]]+)\]\(([^)]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>',
  );
  // Bold-italic ***...*** must run before ** and * so it captures the inner-
  // most semantics correctly.
  t = t.replace(/\*\*\*(.+?)\*\*\*/gs, "<strong><em>$1</em></strong>");
  t = t.replace(/\*\*(.+?)\*\*/gs, "<strong>$1</strong>");
  t = t.replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, "<em>$1</em>");
  t = t.replace(
    /`([^`\n]+)`/g,
    '<code style="background:var(--color-bg-secondary);padding:1px 5px;border-radius:3px;font-size:.85em">$1</code>',
  );

  const lines = t.split("\n");
  const out: string[] = [];
  let listTag: "" | "ul" | "ol" = "";
  const closeList = () => {
    if (listTag) {
      out.push("</" + listTag + ">");
      listTag = "";
    }
  };

  const splitRow = (row: string): string[] => {
    let s = row.trim();
    if (s.startsWith("|")) s = s.slice(1);
    if (s.endsWith("|")) s = s.slice(0, -1);
    return s.split("|").map((c) => c.trim());
  };
  const isTableRow = (s: string) => /\|/.test(s) && s.trim().startsWith("|");
  const isAlignRow = (s: string) =>
    /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)+\|?\s*$/.test(s);

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // GitHub-flavored pipe tables
    if (isTableRow(line) && i + 1 < lines.length && isAlignRow(lines[i + 1])) {
      closeList();
      const header = splitRow(line);
      const aligns = splitRow(lines[i + 1]).map((c) => {
        const l = c.startsWith(":");
        const r = c.endsWith(":");
        if (l && r) return "center";
        if (r) return "right";
        return "left";
      });
      const bodyRows: string[][] = [];
      let j = i + 2;
      while (j < lines.length && isTableRow(lines[j])) {
        bodyRows.push(splitRow(lines[j]));
        j++;
      }
      const thStyle =
        "padding:6px 10px;border-bottom:1px solid var(--color-border);font-weight:600;color:var(--color-text-heading);background:var(--color-bg-secondary)";
      const tdStyle =
        "padding:6px 10px;border-bottom:1px solid var(--color-border-light)";
      let html =
        '<div style="overflow-x:auto;margin:10px 0"><table style="border-collapse:collapse;width:100%;font-size:0.85em"><thead><tr>';
      header.forEach((h, idx) => {
        html += `<th style="${thStyle};text-align:${aligns[idx] ?? "left"}">${h}</th>`;
      });
      html += "</tr></thead><tbody>";
      bodyRows.forEach((row) => {
        html += "<tr>";
        header.forEach((_, idx) => {
          const cell = row[idx] ?? "";
          html += `<td style="${tdStyle};text-align:${aligns[idx] ?? "left"}">${cell}</td>`;
        });
        html += "</tr>";
      });
      html += "</tbody></table></div>";
      out.push(html);
      i = j - 1;
      continue;
    }

    const hm = line.match(/^(#{1,4})\s+(.+)/);
    if (hm) {
      closeList();
      const lvl = hm[1].length;
      // Sizes are scaled so even h4 stays clearly above body text and the
      // common ## / ### read as real headers (h1 ~1.47em, h2 ~1.34, h3 ~1.21,
      // h4 ~1.08), and weight 700 keeps them bold against the medium-weight body.
      out.push(
        `<h${lvl} style="margin:18px 0 7px;font-size:${1.6 - lvl * 0.13}em;font-weight:700;line-height:1.25;color:var(--color-text-heading)">${hm[2]}</h${lvl}>`,
      );
      continue;
    }
    if (/^---+$/.test(line.trim())) {
      closeList();
      out.push(
        '<hr style="border:none;border-top:1px solid var(--color-border);margin:12px 0">',
      );
      continue;
    }
    const bul = line.match(/^[-*]\s+(.+)/);
    if (bul) {
      if (listTag !== "ul") {
        closeList();
        out.push('<ul style="margin:6px 0 6px 18px">');
        listTag = "ul";
      }
      out.push("<li>" + bul[1] + "</li>");
      continue;
    }
    const num = line.match(/^\d+\.\s+(.+)/);
    if (num) {
      if (listTag !== "ol") {
        closeList();
        out.push('<ol style="margin:6px 0 6px 18px">');
        listTag = "ol";
      }
      out.push("<li>" + num[1] + "</li>");
      continue;
    }
    if (line.trim() === "") {
      closeList();
      continue;
    }
    closeList();
    out.push('<p style="margin:4px 0">' + line + "</p>");
  }
  closeList();
  let result = out.join("");
  // Restore the stashed fenced code blocks (already safe HTML built above).
  result = result.replace(
    /__CODE_(\d+)__/g,
    (_m, idx) => codeBlocks[Number(idx)] || "",
  );
  return result;
}
